import { type Action, type ActionCreator, type Store } from '@ngrx/store';
import { map, type Observable, withLatestFrom } from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { getWorkflowTerminators } from '@blaze-shared/workflow-domain';

import {
  runWorkflowDebuggerSuccess,
  workflowDebuggerNodeSuccess,
  workflowDebuggerRunNextNode,
} from './workflow-debugger.actions';
import { selectWfDebugInfo } from './workflow-debugger.selectors';

type ActionWithType = Action & { type: string };

function isOfType<AC extends ActionCreator<string, (...args: any[]) => ActionWithType>>(
  action: Action,
  creator: AC,
): action is ReturnType<AC> {
  return creator.type === action.type;
}

/**
 * Turns each workflow step the engine is about to apply into a debugger step, while a debug session
 * owns that execution. Bound to the engine's `WorkflowStepDebugger` hook.
 */
export function debugMonitor(store: Store) {
  return (source: Observable<Action>): Observable<Action> => {
    return source.pipe(
      withLatestFrom(store.select(selectWfDebugInfo)),
      map(([action, debugInfo]) => {
        if (
          !isOfType(action, runtimeActions.applyWorkflowNode) &&
          !isOfType(action, runtimeActions.resumeWorkflowNode)
        ) {
          return action;
        }

        const lastFinishedNode = [...(debugInfo.nodeExecutionHistory ?? [])].pop();

        const prevNodeId = action.prevNodeId ?? lastFinishedNode?.nodeId;

        const { executionId } = debugInfo;
        if (executionId !== action.workflowExecutionId) {
          return action;
        }

        const terminators = action.workflow ? getWorkflowTerminators(action.workflow) : [];
        const isLastNode = terminators.length == 0 || terminators.some((t) => t.id === prevNodeId);

        const isFinish = isLastNode && !action.resumeToken;
        if (isFinish) {
          return runWorkflowDebuggerSuccess({
            workflowExecutionId: action.workflowExecutionId,
            nodeId: prevNodeId,
            prevNodeId: prevNodeId,
            nodePayload: action,
          });
        }
        const nodeId = action.workflowNode?.id;
        //continue automatically on foreach loops.
        if (!nodeId && !action.isWorkflowCancelled && action.resumeToken) {
          return workflowDebuggerRunNextNode({ nextNodePayload: action });
        }
        return workflowDebuggerNodeSuccess({ nodeId, nodePayload: action });
      }),
    );
  };
}
